import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Lock, Mail, Eye, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function LoginPage() {
  const navigate = useNavigate();
  const [isRegister, setIsRegister] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [form, setForm] = useState({ email: "", password: "" });

  useEffect(() => {
    // Check if admin exists and if user is already logged in
    checkAuth();
  }, []);

  const checkAuth = async () => {
    // Check if already logged in
    const token = localStorage.getItem("admin_token");
    if (token) {
      try {
        await axios.get(`${API}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        navigate("/admin");
        return;
      } catch {
        localStorage.removeItem("admin_token");
      }
    }

    // Check if admin exists
    try {
      const res = await axios.get(`${API}/auth/check`);
      setIsRegister(!res.data.admin_exists);
    } catch (error) {
      console.error("Error checking admin:", error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const endpoint = isRegister ? "/auth/register" : "/auth/login";
      const res = await axios.post(`${API}${endpoint}`, form);
      
      localStorage.setItem("admin_token", res.data.access_token);
      toast.success(isRegister ? "Cuenta creada exitosamente" : "Bienvenido");
      navigate("/admin");
    } catch (error) {
      const message = error.response?.data?.detail || "Error de autenticación";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <h2 className="text-2xl md:text-3xl font-black text-black">
            Darkar Company Studios
          </h2>
          <p className="text-gray-500 mt-2 font-medium">Panel de Administración</p>
        </div>

        {/* Login Card */}
        <div className="bg-white border border-gray-200 rounded-2xl p-8 shadow-lg">
          <h1 className="text-2xl font-bold text-black mb-6">
            {isRegister ? "Crear cuenta de Admin" : "Iniciar Sesión"}
          </h1>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder="admin@ejemplo.com"
                  required
                  className="bg-gray-50 border-gray-200 text-black pl-10 focus:border-[#E91E8C] focus:ring-[#E91E8C]"
                  data-testid="email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm text-gray-600 mb-2 font-medium">Contraseña</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <Input
                  type={showPassword ? "text" : "password"}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  className="bg-gray-50 border-gray-200 text-black pl-10 pr-10 focus:border-[#E91E8C] focus:ring-[#E91E8C]"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-black hover:bg-gray-800 text-white font-semibold py-6 rounded-full"
              data-testid="submit-btn"
            >
              {loading ? "Cargando..." : isRegister ? "Crear Cuenta" : "Entrar"}
            </Button>
          </form>

          {isRegister && (
            <p className="text-gray-500 text-sm mt-6 text-center">
              Esta será la única cuenta de administrador
            </p>
          )}
        </div>

        {/* Back to Home */}
        <div className="text-center mt-6">
          <a href="/" className="text-gray-500 hover:text-black text-sm font-medium">
            ← Volver al inicio
          </a>
        </div>
      </div>
    </div>
  );
}
